/**
 * The handlers of toast actions. The store holds only serializable data, so a toast names its
 * action by id (`ToastAction.actionId`) and the handler lives here. A mounted hook registers it
 * (`useToastAction`). An action with no handler does nothing: the toast only closes.
 */
type ToastActionHandler = () => void;

const handlers = new Map<string, ToastActionHandler>();

/** Registers the handler of an action id. Returns the function that removes this handler. */
export function registerToastAction(actionId: string, handler: ToastActionHandler): () => void {
  handlers.set(actionId, handler);
  return () => {
    // A newer handler of the same id stays (two mounted pages during a route change).
    if (handlers.get(actionId) === handler) {
      handlers.delete(actionId);
    }
  };
}

/** Runs the handler of an action id. Returns false when no handler is registered. */
export function runToastAction(actionId: string): boolean {
  const handler = handlers.get(actionId);
  if (handler === undefined) {
    return false;
  }
  handler();
  return true;
}
